/**
 * 图表调色板（docs/UI设计.md §6.2）
 *
 * lightweight-charts 不读 CSS 变量，主题切换时必须同步替换这些常量。
 * 不要把颜色写成 CSS 变量引用 —— 库只接受具体色值。
 */

export type ThemeName = 'dark' | 'light';

/**
 * 价格格式化：去掉尾部零。
 *
 * 纵轴刻度原本显示 `3810.00`，指数动辄四位数，两个多余的 0 与小数点
 * 白占纵轴宽度，挤压绘图区。
 *
 * ⚠️ v5 的 `priceFormat.formatter` **同时**作用于纵轴刻度与十字线价格标签，
 *   `tickmarksFormatter` 才是只管刻度的那个。故这里两者共用同一格式以保持
 *   一致 —— 刻度与光标标签数字形态相同，才不会读错量级。若改用
 *   tickmarksFormatter 单独给刻度去零，十字线标签会变回两位小数，两者不一致。
 */
export function formatPrice(v: number): string {
  if (!Number.isFinite(v)) return '—';
  // 先 toFixed(2) 再裁尾零：既去掉了尾零，又避免浮点误差把 3810 显示成 3809.9999
  return v.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * 价格轴标尺**整数**格式（用户拍板：3850.00 显示 3850，小数不要）。
 *
 * 只用于图表的 `priceFormat.formatter`（v5 里它同时作用于纵轴刻度与
 * 十字线价格标签，两者同形态才不会读错量级）。读数条/头部大数字等
 * 展示位仍用 formatPrice（两位小数）—— 那是显示精度，不是标尺刻度。
 */
export function formatPriceInt(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return String(Math.round(v));
}

export interface ChartTheme {
  grid: string;
  background: string;
  text1: string;
  text2: string;
  text3: string;
  border: string;
  crosshair: string;

  up: string;
  down: string;
  volumeUp: string;
  volumeDown: string;

  ma: Record<5 | 10 | 20 | 60 | 120, string>;
  boll: string;
  bbi: string;

  pe: string;
  dividend: string;

  /** 分时主线与均价线 */
  minuteLine: string;
  averageLine: string;
  prevClose: string;
  /** 5 日分时：每个交易日一条线的配色（按天循环） */
  band: string[];
}

export const CHART_THEMES: Record<ThemeName, ChartTheme> = {
  dark: {
    grid: '#1A2030',
    background: '#0B0E14',
    text1: '#D7DBE4',
    text2: '#8B93A7',
    text3: '#7C869C',
    border: '#242A38',
    crosshair: '#4A5265',

    up: '#F1474D',
    down: '#14B143',
    volumeUp: 'rgba(241,71,77,0.5)',
    volumeDown: 'rgba(20,177,67,0.5)',

    ma: {
      5: '#F0B90B',
      10: '#4D9FFF',
      20: '#C77DFF',
      60: '#2DD4BF',
      120: '#FF8A65',
    },
    boll: '#7C8AA8',
    bbi: '#E8A33D',

    pe: '#5B8DEF',
    dividend: '#D96BA0',

    minuteLine: '#D7DBE4',
    averageLine: '#F0B90B',
    prevClose: '#8B93A7',
    band: ['#F0B90B', '#4D9FFF', '#C77DFF', '#2DD4BF', '#FF8A65'],
  },
  light: {
    grid: '#EDF0F5',
    background: '#FFFFFF',
    text1: '#16191F',
    text2: '#545C6B',
    text3: '#687183',
    border: '#E3E7EE',
    crosshair: '#AAB2C0',

    up: '#D92930',
    down: '#0D8234',
    volumeUp: 'rgba(217,41,48,0.45)',
    volumeDown: 'rgba(13,130,52,0.45)',

    ma: {
      5: '#C8901A',
      10: '#1A66E8',
      20: '#8B47D6',
      60: '#0E9384',
      120: '#D9541E',
    },
    boll: '#8A93A6',
    bbi: '#B4761A',

    pe: '#1A66E8',
    dividend: '#C23D8E',

    minuteLine: '#16191F',
    averageLine: '#C8901A',
    prevClose: '#6B7382',
    band: ['#C8901A', '#1A66E8', '#8B47D6', '#0E9384', '#D9541E'],
  },
};

export const getChartTheme = (name: ThemeName): ChartTheme => CHART_THEMES[name];

/** 涨跌色语义：全应用统一红涨绿跌（含港股指数） */
export const upDown = (value: number): 'up' | 'down' | 'flat' =>
  value > 0 ? 'up' : value < 0 ? 'down' : 'flat';